import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Wizard } from "components/wizard";
import { GridLayout } from "components/grid/GridLayout";
import {
    FormCheckbox,
    FormCodeInput,
    FormTextInput,
    FormCanvas,
    FormRadio,
    FormDateOfBirthPicker,
} from "components/form-fields";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import {
    AlertConfirm,
    NotificationInline,
    NotificationToaster,
} from "components/notifications";
import { useValidation } from "hooks/useValidation";
import { LicenceCheckingConsentSuccess } from "pages/licence-checking/consent/LicenceCheckingConsentSuccess";
import { LicenceCheckingConsentReject } from "pages/licence-checking/consent/LicenceCheckingConsentReject";
import { LicenceCheckingConsentError } from "pages/licence-checking/consent/LicenceCheckingConsentError";
import { Intent } from "@blueprintjs/core";
import moment from "moment";
import classNames from "classnames";
import "pages/licence-checking/consent/LicenceCheckingConsent.css";

// Named steps used to drive both the rendered wizard children and the step-specific gating logic.
// orderedSteps inside the component varies by mode; the wizard reads currentStep by name rather
// than comparing against numeric indices.
const STEP_MANAGER = "manager";
const STEP_WELCOME = "welcome";
const STEP_DETAILS = "details";
const STEP_SIGNATURE = "signature";

export function DeclarationWizard(
    {
        loadDetails,
        saveDetails,
        refuseDetails = null,
        showManagerConfirmation = false,
        onLoadError = null,
        onSaveSuccess = null,
        onCancel = null
    }
) {
    const yearDigit = 3;
    const endOfSurname = 5;
    const endOfFirstName = 11;
    const endOfMiddleName = 12;
    const endOfGeneratedLicence = 13;
    const licenceRequiredLength = 16;
    const femaleIncrease = 50;
    const ignoreLast3Characters = -3;

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState(true);
    const [wizardIsAllowedBack, setWizardIsAllowedBack] = useState(true);
    const [isValid, errors, validate] = useValidation();
    const [isConsentSuccess, setIsConsentSuccess] = useState(false);
    const [isConsentRejected, setIsConsentRejected] = useState(false);
    const [isConsentError, setIsConsentError] = useState(false);
    const [
        isCurrentAddressSameAsLicenceAddress,
        setIsCurrentAddressSameAsLicenceAddress,
    ] = useState(true);
    const [isRefuseConsentModalVisible, setIsRefuseConsentModalVisible] =
        useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const [consentId, setConsentId] = useState(0);
    const [accountName, setAccountName] = useState("");
    const [driverName, setDriverName] = useState(null);
    const [managerConfirmed, setManagerConfirmed] = useState(false);
    const [syncDetailsToLicenceNumber, setSyncDetailsToLicenceNumber] =
        useState(true);
    const [licenceNumber, setLicenceNumber] = useState("                ");
    const [calculatedLicenceNumber, setCalculatedLicenceNumber] =
        useState("                ");
    const [showLicenceNumberWarning, setShowLicenceNumberWarning] =
        useState(false);
    const [validLicence, setValidLicence] = useState(false);
    const [userValues, setUserValues] = useState({
        firstName: "",
        middleName: "",
        surname: "",
        dateOfBirth: "",
        homeAddress1: "",
        homeAddress2: "",
        homeAddress3: "",
        homeTown: "",
        homePostcode: "",
        licenceAddress1: "",
        licenceAddress2: "",
        licenceAddress3: "",
        licenceTown: "",
        licencePostcode: "",
        signature: "",
        gender: null,
    });

    const orderedSteps = showManagerConfirmation
        ? [STEP_MANAGER, STEP_WELCOME, STEP_DETAILS, STEP_SIGNATURE]
        : [STEP_WELCOME, STEP_DETAILS, STEP_SIGNATURE];
    const currentStep = orderedSteps[wizardStep];

    useEffect(() => {
        loadInitialDetails();
    }, []);

    useEffect(checkUserValues, [
        userValues,
        isValid,
        wizardStep,
        isLoading,
        isCurrentAddressSameAsLicenceAddress,
    ]);

    useEffect(() => {
        if (currentStep === STEP_MANAGER) {
            setWizardValid(managerConfirmed);
            return;
        }
        if (currentStep === STEP_WELCOME) {
            setWizardValid(true);
            return;
        }
        setWizardValid(isValid && validLicence);
    }, [currentStep, managerConfirmed, isValid, validLicence]);

    useEffect(() => {
        let temp = licenceNumber;

        //Only allow alpha characters in the names

        const nonAlphaRegex = /[^a-zA-Z]/g;

        const sanitisedSurname = userValues.surname.trim().replace(nonAlphaRegex, "");
        let sanitisedFirstName = userValues.firstName.trim();
        const sanitisedMiddleName = userValues.middleName.trim().replace(nonAlphaRegex, "");

        //the first part should be salutation, so we can ignore it
        const salutations = ["MR", "MRS", "MS", "DR", "MISS", "MASTER"];

        const firstNameParts = sanitisedFirstName.split(" ");

        if (salutations.indexOf(firstNameParts[0].toUpperCase()) > -1) {
            firstNameParts.shift();
            sanitisedFirstName = firstNameParts.join(" ");
        }

        sanitisedFirstName = sanitisedFirstName.replace(nonAlphaRegex, "");

        if (sanitisedSurname !== "") {
            temp =
                sanitisedSurname
                    .substring(0, endOfSurname)
                    .padEnd(endOfSurname, "9") + temp.substring(endOfSurname);
        } else {
            temp = "     " + temp.substring(endOfSurname);
        }

        if (userValues.dateOfBirth) {
            let tempMonth = moment(userValues.dateOfBirth).format("MM");
            if (userValues.gender === false) {
                tempMonth = (parseInt(tempMonth) + femaleIncrease).toString();
            }

            temp = `${temp.substring(0, endOfSurname)}${moment(
                userValues.dateOfBirth
            )
                .format("YYYY")
                .substring(2, yearDigit)}${tempMonth}${moment(
                userValues.dateOfBirth
            ).format("DD")}${moment(userValues.dateOfBirth)
                .format("YYYY")
                .substring(yearDigit)}${temp.substring(endOfFirstName)}`;
        }

        if (sanitisedFirstName !== "") {
            temp = `${temp.substring(
                0,
                endOfFirstName
            )}${sanitisedFirstName.substring(0, 1)}${temp.substring(
                endOfMiddleName
            )}`;
        } else {
            temp = `${temp.substring(0, endOfFirstName)} ${temp.substring(
                endOfMiddleName
            )}`;
        }

        if (
            sanitisedFirstName.length > 1 &&
            sanitisedMiddleName.length === 0 &&
            sanitisedSurname.length > 1
        ) {
            temp = `${temp.substring(0, endOfMiddleName)}9${temp.substring(
                endOfGeneratedLicence
            )}`;
        } else if (sanitisedMiddleName !== "") {
            temp = `${temp.substring(
                0,
                endOfMiddleName
            )}${sanitisedMiddleName.substring(0, 1)}${temp.substring(
                endOfGeneratedLicence
            )}`;
        } else {
            temp = `${temp.substring(0, endOfMiddleName)} ${temp.substring(
                endOfGeneratedLicence
            )}`;
        }

        setCalculatedLicenceNumber(temp);

        if (!syncDetailsToLicenceNumber) {
            return;
        }

        if (licenceNumber.replace(/\s/g, "").length === licenceRequiredLength) {
            setSyncDetailsToLicenceNumber(false);
            return;
        }

        setLicenceNumber(temp);
    }, [userValues, syncDetailsToLicenceNumber]);

    useEffect(() => {
        if (
            userValues.firstName === "" ||
            userValues.surname === "" ||
            userValues.dateOfBirth === "" ||
            userValues.gender === null
        ) {
            return;
        }

        // Compare ignoring the last 3 characters
        if (
            licenceNumber?.slice(0, ignoreLast3Characters)?.toLowerCase() !==
            calculatedLicenceNumber?.slice(0, ignoreLast3Characters)?.toLowerCase()
        ) {
            setShowLicenceNumberWarning(true);
        } else {
            setShowLicenceNumberWarning(false);
        }
    }, [licenceNumber, userValues]);

    useEffect(
        function () {
            setValidLicence(
                licenceNumber.indexOf(" ") === -1 &&
                    licenceNumber.length === licenceRequiredLength
            );
        },
        [licenceNumber]
    );

    function checkUserValues() {
        const minEmployeeAge = 17;
        const maxEmployeeAge = 100;
        let rules = [];
        let externalRules = [];

        if (currentStep === STEP_DETAILS || currentStep === STEP_SIGNATURE) {
            rules.push({ fieldName: "firstName", required: true });
            rules.push({ fieldName: "surname", required: true });
            rules.push({
                fieldName: "dateOfBirth",
                required: true,
                type: "date",
                minValue: moment().subtract(maxEmployeeAge, "years").toDate(),
                maxValue: moment().subtract(minEmployeeAge, "years").toDate()
            });
            rules.push({ fieldName: "gender", required: true });
            rules.push({ fieldName: "homeAddress1", required: true });
            rules.push({ fieldName: "homeTown", required: true });
            rules.push({ fieldName: "homePostcode", required: true });

            if (!isCurrentAddressSameAsLicenceAddress) {
                rules.push({ fieldName: "licenceAddress1", required: true });
                rules.push({ fieldName: "licenceTown", required: true });
                rules.push({ fieldName: "licencePostcode", required: true });
            }

            if (currentStep === STEP_SIGNATURE) {
                rules.push({ fieldName: "signature", required: true });
            }


            const licenceNameRegex = /^[a-z\-' ]*$/i;
            const licenceNameRegexErrorMessage = "Value can only contain characters from the UK alphabet, as well as spaces, hyphens and apostrophes.";

            externalRules.push({ fieldName: "firstName", valid: licenceNameRegex.test(userValues.firstName), errorMessage: licenceNameRegexErrorMessage});
            externalRules.push({ fieldName: "middleName", valid: licenceNameRegex.test(userValues.middleName), errorMessage: licenceNameRegexErrorMessage});
            externalRules.push({ fieldName: "surname", valid: licenceNameRegex.test(userValues.surname), errorMessage: licenceNameRegexErrorMessage});

        }

        validate(rules, userValues, externalRules);
    }

    // Surface the backend's validation message for a 400 response; fall back to a generic
    // message for every other failure. Both saveConsent and submitOnSiteDeclaration reject
    // with the raw axios error so the status is always available here.
    function resolveSaveErrorMessage(error) {
        const httpBadRequest = 400;
        if (error?.response?.status === httpBadRequest && error?.response?.data) {
            return error.response.data;
        }
        return "Could not save your declaration, please try again.";
    }

    function loadInitialDetails() {
        loadDetails()
            .then(
                (consent) => {
                    setConsentId(consent.id);
                    setAccountName(consent.accountName);

                    if (consent.driverName) {
                        setDriverName(consent.driverName);
                    } else if (consent.declarationDetails) {
                        // Fall back to derived name from declaration details when caller does not supply driverName explicitly.
                        const derivedFirstName = consent.declarationDetails.firstName ?? "";
                        const derivedSurname = consent.declarationDetails.lastName ?? "";
                        const derivedName = `${derivedFirstName} ${derivedSurname}`.trim();
                        if (derivedName !== "") {
                            setDriverName(derivedName);
                        }
                    }

                    if (consent.declarationDetails) {
                        setSyncDetailsToLicenceNumber(false);

                        setUserValues({
                            firstName: consent.declarationDetails.firstName,
                            middleName: consent.declarationDetails.middleName,
                            surname: consent.declarationDetails.lastName,
                            dateOfBirth: consent.declarationDetails.dateOfBirth,
                            homeAddress1: consent.declarationDetails.address1,
                            homeAddress2: consent.declarationDetails.address2,
                            homeAddress3: consent.declarationDetails.address3,
                            homeTown: consent.declarationDetails.town,
                            homePostcode: consent.declarationDetails.postcode,
                            licenceAddress1: "",
                            licenceAddress2: "",
                            licenceAddress3: "",
                            licenceTown: "",
                            licencePostcode: "",
                            signature: "",
                            gender: consent.declarationDetails.gender.toUpperCase().startsWith("M"),
                        });


                        setLicenceNumber(consent.declarationDetails.licenceNumber);

                    }
                },
                (error) => {
                    if (onLoadError != null) {
                        onLoadError(error);
                        return;
                    }
                    setIsConsentError(true);
                }
            )
            .catch((error) => {
                if (onLoadError != null) {
                    onLoadError(error);
                    return;
                }
                setIsConsentError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function updateUserValues(key, value) {
        setUserValues({
            ...userValues,
            [key]: value,
        });
    }

    function onWizardChange(step) {
        setWizardStep(step);
    }

    function onWizardFinish() {
        setIsSaving(true);
        setWizardIsAllowedBack(false);

        let consent = {
            ...userValues,
            licenceNumber: licenceNumber,
        };

        if (isCurrentAddressSameAsLicenceAddress) {
            consent = {
                ...consent,
                licenceAddress1: userValues.homeAddress1,
                licenceAddress2: userValues.homeAddress2,
                licenceAddress3: userValues.homeAddress3,
                licenceTown: userValues.homeTown,
                licencePostcode: userValues.homePostcode,
            };
        }

        saveDetails(consent, { consentId: consentId })
            .then(
                () => {
                    if (onSaveSuccess != null) {
                        onSaveSuccess();
                        return;
                    }
                    setIsConsentSuccess(true);
                },
                (error) => {
                    NotificationToaster.show(Intent.DANGER, resolveSaveErrorMessage(error));
                }
            )
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, resolveSaveErrorMessage(error));
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function refuseConsent() {
        if (refuseDetails == null) {
            return;
        }

        setIsSaving(true);

        // The wrapper builds the actual refuse payload; the wizard only exposes the consentId context.
        refuseDetails({ consentId: consentId })
            .then(
                () => {
                    hideRefuseConsentModal();
                    setIsConsentRejected(true);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not refuse your declaration, please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not refuse your declaration, please try again."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function showRefuseConsentModal() {
        setIsRefuseConsentModalVisible(true);
    }

    function hideRefuseConsentModal() {
        setIsRefuseConsentModalVisible(false);
    }

    function showCancelModal() {
        setIsCancelModalVisible(true);
    }

    function hideCancelModal() {
        setIsCancelModalVisible(false);
    }

    function confirmCancel() {
        hideCancelModal();
        if (onCancel != null) {
            onCancel();
        }
    }

    // The manager-confirmation step copy. Falls back to "the driver" when no driver name has been provided by the caller.
    const driverDisplayName = driverName != null && driverName !== "" ? driverName : "the driver";

    return (
        <div className="licence-checking-form">
            <AlertConfirm
                isOpen={isRefuseConsentModalVisible}
                title="Refuse Declaration"
                onConfirm={refuseConsent}
                onCancel={hideRefuseConsentModal}
                confirmButtonText="Refuse Declaration"
                cancelButtonText="Cancel"
            >
                <p>
                    Are you sure you want to refuse your declaration for
                    your employer to check your driver record information?
                </p>
            </AlertConfirm>

            <AlertConfirm
                isOpen={isCancelModalVisible}
                title="Cancel Declaration"
                onConfirm={confirmCancel}
                onCancel={hideCancelModal}
                confirmButtonText="Discard Declaration"
                cancelButtonText="Continue Declaration"
            >
                <p>
                    Are you sure you want to cancel? Any details you have
                    entered will be lost.
                </p>
            </AlertConfirm>

            <ShowHide
                evaluator={isConsentSuccess}
                show={<LicenceCheckingConsentSuccess />}
            />
            <ShowHide
                evaluator={isConsentRejected}
                show={<LicenceCheckingConsentReject />}
            />

            <ShowHide
                evaluator={isConsentError}
                show={<LicenceCheckingConsentError />}
            />

            <ShowHide
                evaluator={
                    !isConsentSuccess &&
                    !isConsentRejected &&
                    !isConsentError
                }
                show={
                    <Fragment>
                        <h2>Licence Checking Declaration Form</h2>
                        <Wizard
                            onStepChange={onWizardChange}
                            onFinish={onWizardFinish}
                            canProceed={wizardValid}
                            loading={isLoading}
                            disabled={isSaving}
                            allowBack={wizardIsAllowedBack}
                            alignButtonsRight
                            additionalButtons={
                                <Fragment>
                                    <ShowHide
                                        evaluator={refuseDetails != null}
                                        show={
                                            <ShowHide
                                                evaluator={currentStep === STEP_WELCOME}
                                                show={
                                                    <Button
                                                        loading={isLoading}
                                                        text="Refuse Declaration"
                                                        intent="danger"
                                                        onClick={showRefuseConsentModal}
                                                    />
                                                }
                                            />
                                        }
                                    />
                                    <ShowHide
                                        evaluator={onCancel != null}
                                        show={
                                            <Button
                                                loading={isLoading}
                                                disabled={isSaving}
                                                text="Cancel"
                                                onClick={showCancelModal}
                                            />
                                        }
                                    />
                                </Fragment>
                            }
                        >
                            {[
                            showManagerConfirmation && (
                                <div className="spacer-bottom" key="manager-confirmation">
                                    <h3
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        Manager Confirmation
                                    </h3>
                                    <p
                                        className={classNames("spacer-bottom-small", {
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        {`This declaration is being completed on-site with the driver. As the manager, you must confirm that you are physically present with ${driverDisplayName} and that the driver from ${accountName} is completing this declaration themselves.`}
                                    </p>
                                    <FormCheckbox
                                        id="declaration-wizard-manager-confirmation"
                                        label="I confirm that I am present with the driver and that the driver is completing this declaration themselves."
                                        checked={managerConfirmed}
                                        onChange={(e) =>
                                            setManagerConfirmed(e.target.checked)
                                        }
                                    />
                                </div>
                            ),
                            <div className="spacer-bottom" key="welcome">
                                <h3
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    Welcome to the Vision Licence Checking
                                    Declaration Process
                                </h3>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    As a responsible employer, {accountName}
                                    , have an obligation to ensure that you
                                    are legal to drive by checking your
                                    licence details as regularly as required
                                    during your employment.
                                </p>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    You are required to complete the driver
                                    declaration form to allow your driving
                                    licence records held by the Driver and
                                    Vehicle Licensing Agency (DVLA) to be
                                    checked.
                                </p>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    The declaration will be valid for 3
                                    years whilst you work for {accountName}.
                                </p>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    To complete the declaration process, you
                                    will need:
                                </p>
                                <ul
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    <li>A valid GB driving licence</li>
                                </ul>
                                <p
                                    className={classNames({
                                        "bp6-skeleton": isLoading,
                                    })}
                                >
                                    To view a processing statement/privacy
                                    policy that summarises what Logistics UK
                                    will request on behalf of {accountName}{" "}
                                    how it will be used, and your rights in
                                    respect of that data at any point,{" "}
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://logistics.org.uk/logistics-uk-privacy-policy/"
                                    >
                                        click here
                                    </a>
                                    .
                                </p>
                                <ShowHide
                                    evaluator={refuseDetails != null}
                                    show={
                                        <p
                                            className={classNames({
                                                "bp6-skeleton": isLoading,
                                            })}
                                        >
                                            Click the next button below to
                                            begin the declaration process. If
                                            the above criteria does not apply
                                            to you then you can click refuse
                                            declaration.
                                        </p>
                                    }
                                    hide={
                                        <p
                                            className={classNames({
                                                "bp6-skeleton": isLoading,
                                            })}
                                        >
                                            Click the next button below to
                                            begin the declaration process.
                                        </p>
                                    }
                                />
                            </div>,
                            <div key="details">
                                <NotificationInline
                                    allowClose={false}
                                    text={
                                        <span>
                                            You must enter all your details <b>exactly</b> as they appear on your driver's licence, if the data is not accurate you will need to complete a new declaration and your company might be charged.
                                        </span>
                                    }
                                    intent={Intent.PRIMARY}
                                    show
                                />
                                <h3>Driver Licence Details</h3>
                                <GridLayout numberOfColumns={3} gap={40}>
                                    <FormTextInput
                                        id="licence-checking-consent-forename"
                                        headingText="Forename"
                                        fill
                                        value={userValues.firstName}
                                        onChange={(e) =>
                                            updateUserValues(
                                                "firstName",
                                                e.target.value
                                            )
                                        }
                                        loading={isLoading}
                                        dangerHelperText={errors.firstName}
                                    />
                                    <FormTextInput
                                        id="licence-checking-consent-middle-name"
                                        headingText="Middle name/s"
                                        fill
                                        value={userValues.middleName}
                                        onChange={(e) =>
                                            updateUserValues(
                                                "middleName",
                                                e.target.value
                                            )
                                        }
                                        loading={isLoading}
                                        dangerHelperText={errors.middleName}

                                    />
                                    <FormTextInput
                                        id="licence-checking-consent-surname"
                                        headingText="Surname"
                                        fill
                                        value={userValues.surname}
                                        onChange={(e) =>
                                            updateUserValues(
                                                "surname",
                                                e.target.value
                                            )
                                        }
                                        loading={isLoading}
                                        dangerHelperText={errors.surname}
                                    />
                                </GridLayout>
                                <GridLayout numberOfColumns={3} gap={40}>
                                    <FormDateOfBirthPicker
                                        id="licence-checking-consent-dob-picker"
                                        headingText="Date of Birth"
                                        value={(!userValues.dateOfBirth || userValues.dateOfBirth === "") ? null : new Date(userValues.dateOfBirth)}
                                        onDateChange={(date) => updateUserValues(
                                            "dateOfBirth",
                                            date ? moment(date).format("YYYY-MM-DD") : null)
                                        }
                                        loading={isLoading}
                                        dangerHelperText={errors.dateOfBirth}
                                    />

                                    <FormRadio
                                        id="licence-gender"
                                        options={[
                                            { value: true, label: "Male" },
                                            {
                                                value: false,
                                                label: "Female",
                                            },
                                        ]}
                                        onChange={(event) =>
                                            updateUserValues(
                                                "gender",
                                                event.target.value ===
                                                    "true"
                                            )
                                        }
                                        headingText="Gender"
                                        selectedValue={userValues.gender}
                                        loading={isLoading}
                                        dangerHelperText={errors.gender}
                                    />
                                </GridLayout>
                                <div className="licence-number-input">
                                    <FormCodeInput
                                        length={16}
                                        value={licenceNumber}
                                        onChange={(value) => {
                                            setLicenceNumber(value);
                                        }}
                                        isLoading={isLoading}
                                        headingText="Driving Licence"
                                        helperText="The driving licence number is automatically generated based on the details you have provided. However, the last 3 characters have to be manually entered."
                                        dangerHelperText={
                                            validLicence
                                                ? null
                                                : "This field is required."
                                        }
                                    />
                                    <NotificationInline
                                        show={showLicenceNumberWarning}
                                        intent={Intent.WARNING}
                                        messages={[
                                            {
                                                message:
                                                    "The driving licence number you have entered does not match the expected values taken from the details you have provided. Please review before continuing.",
                                            },
                                        ]}
                                    />
                                </div>
                                <h3>Current Address</h3>
                                <GridLayout numberOfColumns={3} gap={40}>
                                    <div>
                                        <FormTextInput
                                            id="licence-checking-consent-address-line-1"
                                            headingText="Address"
                                            placeholder="Line 1"
                                            fill
                                            loading={isLoading}
                                            value={userValues.homeAddress1}
                                            onChange={(e) =>
                                                updateUserValues(
                                                    "homeAddress1",
                                                    e.target.value
                                                )
                                            }
                                            dangerHelperText={
                                                errors.homeAddress1
                                            }
                                        />
                                        <FormTextInput
                                            id="licence-checking-consent-address-line-2"
                                            placeholder="Line 2 (optional)"
                                            fill
                                            loading={isLoading}
                                            value={userValues.homeAddress2}
                                            onChange={(e) =>
                                                updateUserValues(
                                                    "homeAddress2",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <FormTextInput
                                            id="licence-checking-consent-address-line-3"
                                            placeholder="Line 3 (optional)"
                                            fill
                                            loading={isLoading}
                                            value={userValues.homeAddress3}
                                            onChange={(e) =>
                                                updateUserValues(
                                                    "homeAddress3",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <FormTextInput
                                        id="licence-checking-consent-town-or-City"
                                        headingText="Town/City"
                                        fill
                                        loading={isLoading}
                                        value={userValues.homeTown}
                                        onChange={(e) =>
                                            updateUserValues(
                                                "homeTown",
                                                e.target.value
                                            )
                                        }
                                        dangerHelperText={errors.homeTown}
                                    />
                                    <FormTextInput
                                        id="licence-checking-consent-postcode"
                                        headingText="Postcode"
                                        fill
                                        loading={isLoading}
                                        value={userValues.homePostcode}
                                        onChange={(e) =>
                                            updateUserValues(
                                                "homePostcode",
                                                e.target.value
                                            )
                                        }
                                        dangerHelperText={
                                            errors.homePostcode
                                        }
                                    />
                                </GridLayout>
                                <div className="spacer-bottom">
                                    <FormCheckbox
                                        id="licence-checking-consent-address-confirmation"
                                        label="This is the same address as the address on my driving licence"
                                        checked={
                                            isCurrentAddressSameAsLicenceAddress
                                        }
                                        onChange={(e) =>
                                            setIsCurrentAddressSameAsLicenceAddress(
                                                e.target.checked
                                            )
                                        }
                                    />
                                </div>
                                <ShowHide
                                    evaluator={
                                        isCurrentAddressSameAsLicenceAddress
                                    }
                                    hide={
                                        <>
                                            <h3>Address on Licence</h3>
                                            <GridLayout
                                                numberOfColumns={3}
                                                gap={40}
                                            >
                                                <div>
                                                    <FormTextInput
                                                        id="licence-checking-consent-address-line-1"
                                                        headingText="Address"
                                                        placeholder="Line 1"
                                                        fill
                                                        loading={isLoading}
                                                        value={
                                                            userValues.licenceAddress1
                                                        }
                                                        onChange={(e) =>
                                                            updateUserValues(
                                                                "licenceAddress1",
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                        dangerHelperText={
                                                            errors.licenceAddress1
                                                        }
                                                    />
                                                    <FormTextInput
                                                        id="licence-checking-consent-address-line-2"
                                                        placeholder="Line 2 (optional)"
                                                        fill
                                                        loading={isLoading}
                                                        value={
                                                            userValues.licenceAddress2
                                                        }
                                                        onChange={(e) =>
                                                            updateUserValues(
                                                                "licenceAddress2",
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                    <FormTextInput
                                                        id="licence-checking-consent-address-line-3"
                                                        placeholder="Line 3 (optional)"
                                                        fill
                                                        loading={isLoading}
                                                        value={
                                                            userValues.licenceAddress3
                                                        }
                                                        onChange={(e) =>
                                                            updateUserValues(
                                                                "licenceAddress3",
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <FormTextInput
                                                    id="licence-checking-consent-town-or-City"
                                                    headingText="Town/City"
                                                    fill
                                                    loading={isLoading}
                                                    value={
                                                        userValues.licenceTown
                                                    }
                                                    onChange={(e) =>
                                                        updateUserValues(
                                                            "licenceTown",
                                                            e.target.value
                                                        )
                                                    }
                                                    dangerHelperText={
                                                        errors.licenceTown
                                                    }
                                                />
                                                <FormTextInput
                                                    id="licence-checking-consent-postcode"
                                                    headingText="Postcode"
                                                    fill
                                                    loading={isLoading}
                                                    value={
                                                        userValues.licencePostcode
                                                    }
                                                    onChange={(e) =>
                                                        updateUserValues(
                                                            "licencePostcode",
                                                            e.target.value
                                                        )
                                                    }
                                                    dangerHelperText={
                                                        errors.licencePostcode
                                                    }
                                                />
                                            </GridLayout>
                                        </>
                                    }
                                />
                            </div>,
                            <div className="spacer-bottom" key="signature">
                                <div className="spacer-bottom">
                                    <h3>Declaration</h3>
                                    <p>
                                        I {userValues.firstName}{" "}
                                        {userValues.surname} confirm that I
                                        have been instructed to work by{" "}
                                        {accountName} and understand they
                                        have engaged Logistics UK to request
                                        my driving record from DVLA for the
                                        lawful purposes of checking my
                                        entitlement to drive in connection
                                        with my work or other legitimate
                                        reasons.
                                    </p>
                                    <p>
                                        I understand DVLA will disclose to
                                        Logistics UK all relevant
                                        information held in the computerised
                                        register of drivers maintained by
                                        DVLA. This includes personal
                                        details, driving entitlements, valid
                                        endorsements and disqualifications
                                        (if relevant) and photo images (in
                                        some circumstances), Certificate of
                                        Professional Competence (CPC) and
                                        Digital Tachograph Card details
                                        (where appropriate).
                                    </p>
                                    <p>
                                        This declaration will expire when I
                                        stop driving in connection with the
                                        company or in any case, three years
                                        from the date of my signature.
                                    </p>
                                </div>

                                <FormCanvas
                                    isLoading={isLoading}
                                    penColour="grey"
                                    headingText="Signature"
                                    helperText="Please sign in the box below by either drawing with your cursor on desktop or tapping on mobile"
                                    onChange={(value) =>
                                        updateUserValues(
                                            "signature",
                                            value.split(",").pop()
                                        )
                                    }
                                    showDate
                                />
                            </div>,
                            ].filter(Boolean)}
                        </Wizard>
                    </Fragment>
                }
            />
        </div>
    );
}

DeclarationWizard.propTypes = {
    loadDetails: PropTypes.func.isRequired,
    saveDetails: PropTypes.func.isRequired,
    refuseDetails: PropTypes.func,
    showManagerConfirmation: PropTypes.bool,
    onLoadError: PropTypes.func,
    onSaveSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
